import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Paper,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Code as CodeIcon,
  Terminal as TerminalIcon,
  DataObject as DataObjectIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import type { EndpointRequest } from "../types";
import { ModernMethodChip } from "./ModernUiComponents";

interface CodeSnippetDialogProps {
  open: boolean;
  onClose: () => void;
  request: EndpointRequest | null;
}

export const CodeSnippetDialog: React.FC<CodeSnippetDialogProps> = ({ open, onClose, request }) => {
  const [tab, setTab] = useState<"axios" | "fetch" | "curl" | "typescript">("axios");
  const [copied, setCopied] = useState(false);

  if (!request) return null;

  const url = request.endpoint || `/api/v1/${request.component.toLowerCase().replace(/\s+/g, "-")}`;
  const method = (request.method || "GET").toUpperCase();

  let payloadObj: unknown = null;
  if (request.payload) {
    try {
      payloadObj = JSON.parse(request.payload);
    } catch {
      payloadObj = request.payload;
    }
  }

  // Generate Axios snippet
  const generateAxios = () => {
    let code = `import apiService from "service/Api";\n\n`;
    code += `// ${request.component}: ${request.task}\n`;
    code += `export const execute${request.component.replace(/[^a-zA-Z0-9]/g, "")} = async (${
      payloadObj ? "payload: any" : ""
    }) => {\n`;
    code += `  try {\n`;
    if (method === "GET") {
      code += `    const response = await apiService.get("${url}"${payloadObj ? `, { params: payload }` : ""});\n`;
    } else if (method === "POST") {
      code += `    const response = await apiService.post("${url}", payload);\n`;
    } else if (method === "PUT") {
      code += `    const response = await apiService.put("${url}", payload);\n`;
    } else if (method === "PATCH") {
      code += `    const response = await apiService.patch("${url}", payload);\n`;
    } else if (method === "DELETE") {
      code += `    const response = await apiService.delete("${url}");\n`;
    }
    code += `    return response.data;\n`;
    code += `  } catch (error) {\n`;
    code += `    console.error("Failed to execute ${request.component} request:", error);\n`;
    code += `    throw error;\n`;
    code += `  }\n`;
    code += `};`;
    return code;
  };

  // Generate Fetch snippet
  const generateFetch = () => {
    let code = `// ${request.component}: ${request.task}\n`;
    code += `const options: RequestInit = {\n`;
    code += `  method: "${method}",\n`;
    code += `  headers: {\n`;
    code += `    "Content-Type": "application/json",\n`;
    code += `    "Authorization": \`Bearer \${token}\`,\n`;
    code += `  },\n`;
    if (payloadObj && method !== "GET") {
      code += `  body: JSON.stringify(${JSON.stringify(payloadObj, null, 4)}),\n`;
    }
    code += `};\n\n`;
    code += `const response = await fetch("${url}", options);\n`;
    code += `const data = await response.json();`;
    return code;
  };

  // Generate cURL snippet
  const generateCurl = () => {
    let code = `curl -X ${method} "${url}" \\\n`;
    code += `  -H "Content-Type: application/json" \\\n`;
    code += `  -H "Authorization: Bearer YOUR_TOKEN"`;
    if (payloadObj && method !== "GET") {
      code += ` \\\n  -d '${JSON.stringify(payloadObj, null, 2)}'`;
    }
    return code;
  };

  // Generate TypeScript Interface
  const generateTypeScript = () => {
    const componentName = request.component.replace(/[^a-zA-Z0-9]/g, "");

    const getTypeString = (val: unknown, indent = "  "): string => {
      if (val === null) return "any";
      if (Array.isArray(val)) {
        if (val.length === 0) return "any[]";
        return `${getTypeString(val[0], indent)}[]`;
      }
      if (typeof val === "object") {
        let res = "{\n";
        const keys = Object.keys(val as Record<string, unknown>);
        for (const k of keys) {
          const childVal = (val as Record<string, unknown>)[k];
          res += `${indent}  ${k}: ${getTypeString(childVal, indent + "  ")};\n`;
        }
        res += `${indent}}`;
        return res;
      }
      return typeof val;
    };

    let code = `/**\n * ${request.component} Request Payload Interface\n * Task: ${request.task}\n */\n`;
    if (payloadObj && typeof payloadObj === "object" && !Array.isArray(payloadObj)) {
      code += `export interface ${componentName}Payload {\n`;
      for (const [k, v] of Object.entries(payloadObj as Record<string, unknown>)) {
        code += `  ${k}?: ${getTypeString(v)};\n`;
      }
      code += `}\n\n`;
    } else {
      code += `export interface ${componentName}Payload {\n  [key: string]: any;\n}\n\n`;
    }

    code += `export interface ${componentName}Response {\n  status: string;\n  message?: string;\n  data?: any;\n}`;
    return code;
  };

  const getActiveCode = () => {
    switch (tab) {
      case "axios":
        return generateAxios();
      case "fetch":
        return generateFetch();
      case "curl":
        return generateCurl();
      case "typescript":
        return generateTypeScript();
      default:
        return "";
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(getActiveCode());
    setCopied(true);
    toast.success("Code snippet copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth slotProps={{ paper: { sx: { borderRadius: "20px" } } }}>
      <DialogTitle
        component="div"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2.5,
          borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: "12px",
              backgroundColor: "primary.main",
              color: "white",
              display: "flex",
            }}
          >
            <CodeIcon fontSize="small" />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="700">
              Code Snippet Generator
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.2 }}>
              <ModernMethodChip method={request.method} />
              <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                {url}
              </Typography>
            </Box>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Tabs
            value={tab}
            onChange={(_, val) => setTab(val)}
            sx={{
              minHeight: 36,
              "& .MuiTab-root": {
                minHeight: 36,
                py: 0.5,
                px: 2,
                fontSize: "0.825rem",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "10px",
              },
            }}
          >
            <Tab label="Axios / API Service" value="axios" icon={<CodeIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
            <Tab label="Fetch API" value="fetch" icon={<CodeIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
            <Tab label="cURL Command" value="curl" icon={<TerminalIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
            <Tab label="TypeScript Interface" value="typescript" icon={<DataObjectIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
          </Tabs>

          <Button
            variant="outlined"
            size="small"
            startIcon={copied ? <CheckIcon color="success" /> : <CopyIcon />}
            onClick={handleCopy}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600 }}
          >
            {copied ? "Copied!" : "Copy Code"}
          </Button>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            backgroundColor: "#1e1e2e",
            color: "#cdd6f4",
            borderRadius: "14px",
            fontFamily: "'Fira Code', 'Consolas', 'Roboto Mono', monospace",
            fontSize: "0.85rem",
            overflowX: "auto",
            position: "relative",
            maxHeight: "420px",
            border: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{getActiveCode()}</pre>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: "1px solid rgba(226, 232, 240, 0.8)" }}>
        <Button onClick={onClose} variant="contained" sx={{ borderRadius: "10px", px: 3, textTransform: "none", fontWeight: 700 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
